/** Strip leaked markdown title / whitespace from a data URI payload. */
export function normalizeDataUri(dataUri: string): string {
  const trimmed = dataUri.trim();
  const commaIdx = trimmed.indexOf(',');
  if (commaIdx < 0) return trimmed;

  const header = trimmed.slice(0, commaIdx);
  const payload = (trimmed.slice(commaIdx + 1).split(/\s+/)[0] ?? '').replace(/[^A-Za-z0-9+/=]/g, '');

  return `${header},${payload}`;
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

/**
 * Decode a `data:image/…;base64,…` URL into a File.
 * Uses `atob` instead of `fetch` — large data URIs fail with ERR_INVALID_URL.
 */
export async function dataUriToFile(dataUri: string, fallbackName = 'pasted-image'): Promise<File> {
  const normalized = normalizeDataUri(dataUri);
  const commaIdx = normalized.indexOf(',');
  const header = commaIdx >= 0 ? normalized.slice(0, commaIdx) : '';

  if (commaIdx < 0 || !/;base64/i.test(header)) {
    throw new Error('Unsupported data URI (expected base64 image)');
  }

  const mimeMatch = /^data:(image\/[a-z0-9.+-]+)/i.exec(header);
  const mime = mimeMatch?.[1] || 'image/png';
  const payload = normalized.slice(commaIdx + 1);
  const bytes = base64ToUint8Array(payload);
  const ext = mime.split('/')[1]?.split('+')[0] || 'png';
  const name = fallbackName.includes('.') ? fallbackName : `${fallbackName}.${ext}`;
  // BlobPart requires ArrayBuffer, not ArrayBufferLike (SharedArrayBuffer).
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);

  return new File([buffer], name, { type: mime });
}
